import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Search, 
  Loader2, 
  Eye, 
  CheckCircle, 
  XCircle, 
  Clock,
  AlertCircle,
  CreditCard,
  Building2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { SortableHeader, useSort } from '../../components/ui/sortable-header';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminFundRequestsPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [totalRequests, setTotalRequests] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const { sortConfig, handleSort, sortItems } = useSort('created_at', 'desc');
  const limit = 20;

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchRequests();
  }, [token, page, statusFilter]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await axios.get(`${API_URL}/api/admin/fund-requests?${params}`, { headers });
      setRequests(response.data.items || []);
      setTotalRequests(response.data.total || 0);
    } catch (err) {
      console.error('Error fetching fund requests:', err);
      toast.error('Failed to load fund requests');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = (request) => {
    setSelectedRequest(request);
    setNewStatus(request.status);
    setAdminNotes(request.admin_notes || '');
    setReviewOpen(true);
  };

  const submitReview = async () => {
    if (!newStatus) {
      toast.error('Please select a status');
      return;
    }
    
    try {
      setSubmitting(true);
      await axios.put(`${API_URL}/api/admin/fund-requests/${selectedRequest.id}`, {
        status: newStatus,
        admin_notes: adminNotes
      }, { headers });
      
      toast.success(`Request ${newStatus === 'approved' ? 'approved' : newStatus === 'rejected' ? 'rejected' : 'updated'}`);
      setReviewOpen(false);
      fetchRequests();
    } catch (err) {
      console.error('Error reviewing request:', err);
      toast.error(err.response?.data?.detail || 'Failed to update request');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="h-4 w-4 text-amber-500" />;
      case 'under_review': return <AlertCircle className="h-4 w-4 text-blue-500" />;
      case 'approved': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'rejected': return <XCircle className="h-4 w-4 text-red-500" />;
      case 'disbursed': return <CheckCircle className="h-4 w-4 text-[#0D3B66]" />;
      default: return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'bg-amber-100 text-amber-800',
      under_review: 'bg-blue-100 text-blue-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      disbursed: 'bg-[#1D2D2A] text-white'
    };
    return variants[status] || 'bg-gray-100 text-gray-800';
  };

  const totalPages = Math.ceil(totalRequests / limit);

  // Summary stats
  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const underReviewCount = requests.filter(r => r.status === 'under_review').length;

  return (
    <div className="space-y-6" data-testid="admin-fund-requests-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#212529] font-['Manrope']">Fund Requests</h1>
          <p className="text-[#6C757D]">Review and approve client funding requests</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#6C757D]">Total Requests</p>
                <p className="text-2xl font-bold text-[#212529]">{totalRequests}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <CreditCard className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#6C757D]">Pending Review</p>
                <p className="text-2xl font-bold text-amber-600">{pendingCount}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
                <Clock className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#6C757D]">Under Review</p>
                <p className="text-2xl font-bold text-blue-600">{underReviewCount}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <AlertCircle className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#6C757D]">This Page</p>
                <p className="text-2xl font-bold text-[#212529]">{requests.length}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center">
                <Building2 className="h-6 w-6 text-gray-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="under_review">Under Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="disbursed">Disbursed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Requests Table */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-12 text-[#6C757D]">
              No fund requests found
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableHeader label="Client" sortKey="company_name" sortConfig={sortConfig} onSort={handleSort} />
                      <SortableHeader label="Amount" sortKey="amount_requested" sortConfig={sortConfig} onSort={handleSort} />
                      <SortableHeader label="Deal Type" sortKey="deal_type" sortConfig={sortConfig} onSort={handleSort} />
                      <SortableHeader label="Tenor" sortKey="expected_tenor_days" sortConfig={sortConfig} onSort={handleSort} />
                      <TableHead>Purpose</TableHead>
                      <SortableHeader label="Submitted" sortKey="created_at" sortConfig={sortConfig} onSort={handleSort} />
                      <SortableHeader label="Status" sortKey="status" sortConfig={sortConfig} onSort={handleSort} className="text-center" />
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortItems(requests).map((request) => (
                      <TableRow key={request.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{request.company_name || request.client_id}</p>
                            <p className="text-xs text-[#6C757D]">{request.client_id}</p>
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">{formatCurrency(request.amount_requested)}</TableCell>
                        <TableCell>{request.deal_type}</TableCell>
                        <TableCell>{request.expected_tenor_days} days</TableCell>
                        <TableCell className="max-w-[200px] truncate">{request.purpose}</TableCell>
                        <TableCell className="text-sm">{formatDate(request.created_at)}</TableCell>
                        <TableCell className="text-center">
                          <Badge className={getStatusBadge(request.status)}>
                            <span className="flex items-center gap-1">
                              {getStatusIcon(request.status)}
                              {request.status.replace('_', ' ')}
                            </span>
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleReview(request)}
                            data-testid={`review-${request.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-[#6C757D]">
                    Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, totalRequests)} of {totalRequests}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm">Page {page} of {totalPages}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Review Fund Request</DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-6">
              {/* Request Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-[#6C757D]">Client</p>
                  <p className="font-medium">{selectedRequest.company_name || selectedRequest.client_id}</p>
                </div>
                <div>
                  <p className="text-sm text-[#6C757D]">Client ID</p>
                  <p className="font-mono">{selectedRequest.client_id}</p>
                </div>
                <div>
                  <p className="text-sm text-[#6C757D]">Amount Requested</p>
                  <p className="text-xl font-bold text-[#212529]">{formatCurrency(selectedRequest.amount_requested)}</p>
                </div>
                <div>
                  <p className="text-sm text-[#6C757D]">Deal Type</p>
                  <p className="font-medium">{selectedRequest.deal_type}</p>
                </div>
                <div>
                  <p className="text-sm text-[#6C757D]">Expected Tenor</p>
                  <p className="font-medium">{selectedRequest.expected_tenor_days} days</p>
                </div>
                <div>
                  <p className="text-sm text-[#6C757D]">Submitted</p>
                  <p className="font-medium">{formatDate(selectedRequest.created_at)}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-[#6C757D]">Purpose</p>
                  <p className="font-medium">{selectedRequest.purpose}</p>
                </div>
                {selectedRequest.supplier_name && (
                  <div>
                    <p className="text-sm text-[#6C757D]">Supplier</p>
                    <p className="font-medium">{selectedRequest.supplier_name}</p>
                  </div>
                )}
                {selectedRequest.customer_name && (
                  <div>
                    <p className="text-sm text-[#6C757D]">Customer</p>
                    <p className="font-medium">{selectedRequest.customer_name}</p>
                  </div>
                )}
                {selectedRequest.product_description && (
                  <div className="col-span-2">
                    <p className="text-sm text-[#6C757D]">Product Description</p>
                    <p className="font-medium">{selectedRequest.product_description}</p>
                  </div>
                )}
                {selectedRequest.collateral_description && (
                  <div className="col-span-2">
                    <p className="text-sm text-[#6C757D]">Collateral / Security</p>
                    <p className="font-medium">{selectedRequest.collateral_description}</p>
                  </div>
                )}
              </div>

              {/* Review Actions */}
              <div className="border-t pt-4 space-y-4">
                <div>
                  <Label htmlFor="status">Update Status</Label>
                  <Select value={newStatus} onValueChange={setNewStatus}>
                    <SelectTrigger data-testid="status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="under_review">Under Review</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                      <SelectItem value="disbursed">Disbursed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="notes">Admin Notes</Label>
                  <Textarea
                    id="notes"
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Add notes about this request..."
                    rows={3}
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={submitReview}
              disabled={submitting}
              className={newStatus === 'approved' ? 'bg-green-600 hover:bg-green-700' : 
                        newStatus === 'rejected' ? 'bg-red-600 hover:bg-red-700' : 
                        'bg-amber-500 hover:bg-amber-600 text-black'}
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {newStatus === 'approved' ? 'Approve Request' : 
               newStatus === 'rejected' ? 'Reject Request' : 
               'Update Status'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminFundRequestsPage;
